<template>

    <div class="auto-suggest-input"
         role="combobox"
         v-bind:aria-expanded="inputElementHasFocus && results && results.length > 0 ? 'true' : 'false'"
         aria-owns="result-listbox"
         aria-haspopup="listbox"
    >

        <select
                v-bind="selectAttributes"
                aria-hidden="true"
        >
            <option v-for="value in selected" v-bind:value="value" selected>{{value}}</option>
        </select>

        <div>
            <div class="input-wrap"
                 @click="$_focusInputFromWrapper"
                 v-bind:class="{'with-selection': selected.length > 0, focused: inputElementHasFocus}"
            >
                <ul class="tag-list" v-if="selected.length > 0">
                    <li v-for="(selected, i) in _selected"
                        @click="$_removeViaSelection(selected.value)"
                    >
                        <span v-html="selected.label"></span>
                        <span aria-label="Unselect" class="remove-tag"><i class="fas fa-times"></i></span>
                    </li>
                </ul>

                <input
                        type="text"
                        autocomplete="disabled"
                        v-bind:placeholder="placeholderText"
                        v-on:input="searchQuery = $event.target.value; $_queryUpdate()"
                        @blur="$_blurInputElement"
                        @focus="$_focusInputElement"
                        @keydown="$_inputKeyDown"

                        aria-autocomplete="list"
                        aria-controls="result-listbox"
                        v-bind:aria-activedescendant="ariaFocusedResult"
                >

                <button
                        v-if="typeof actionButton.content === 'string' && typeof actionButton.callback === 'function'"
                        v-bind:aria-label="actionButton.ariaLabel"
                        v-bind:title="actionButton.ariaLabel"
                        v-html="actionButton.content"
                        @click="$_attemptActionButtonCallback"
                        class="action-button"></button>
            </div>
            <div v-bind:class="{
                results: true,
                'loading-results': _isSearching,
                'has-results': inputElementHasFocus && results && results.length > 0,
                'no-results': !_isSearching && inputElementHasFocus && noResults === true
            }">

                <div class="throbber" aria-hidden="true">
                    <i class="fas fa-circle-notch fa-spin"></i>
                </div>
                <p class="no-results-message" v-if="inputElementHasFocus && noResults === true">No results found for <em>{{searchQuery}}</em></p>
                <ol v-if="results && results.length > 0"
                    role="listbox"
                >
                    <li class="not-selectable" v-if="suggestedSearch" @click="$_maybeQuerySuggestion">
                        <span class="suggested-search-title" v-html="suggestedSearch.label"></span>
                        <span class="suggested-search-detail"><small v-html="suggestedSearch.detail"></small></span>
                        <span class="suggested-search-supporting" v-if="suggestedSearch.supporting">
                            <em><small>Suggestions:</small> </em>
                            <small v-html="suggestedSearch.supporting"></small>
                        </span>
                    </li>
                    <li v-bind:class="{selectable: true, selected: _selected[result.value]}"
                        v-for="(result, i) in results"
                        @click="$_toggleSelectionFromResults(i)"
                        v-bind:data-result-index="i"
                        v-bind:id="'selectable-result-' + i"
                        role="option"
                    >
                        <span class="result-title" v-html="result.label"></span>
                        <span class="result-detail" v-if="result.detail"><small
                                v-html="result.detail"></small></span>
                    </li>
                </ol>
            </div>
        </div>
    </div>
</template>
<script>
    const Api = require('../../../../global/js/modules/api');

    const events = {
        searchStarted: 'auto-suggest-input:search-started',
        searchCompleted: 'auto-suggest-input:search-completed',
        selectionUpdated: 'auto-suggest-input:selection-updated',
    };

    const Key = {
        BACKSPACE: 8,
        TAB: 9,
        RETURN: 13,
        ESC: 27,
        SPACE: 32,
        PAGE_UP: 33,
        PAGE_DOWN: 34,
        END: 35,
        HOME: 36,
        LEFT: 37,
        UP: 38,
        RIGHT: 39,
        DOWN: 40,
        DELETE: 46
    };

    const IsAriaListboxInteraction = (code) => {
        switch (code) {
            case Key.UP:
            case Key.DOWN:
            case Key.RETURN:
            case Key.TAB:
            case Key.ESC:
                return true;
            default:
                return false;
        }
    };

    function _dispatchDomEvent($el, eventName, data = null) {
        let eventData = data === null
            ? {}
            : {detail: data};

        eventData.bubbles = true;

        let event;

        event = new CustomEvent(eventName, eventData);

        $el.dispatchEvent(event);
    }

    export default {
        components: {},
        props: {
            /**
             * Should variable debug display be enabled
             */
            debugPanel: {
                type: Boolean,
                default: false,
            },
            /**
             * The name of the input
             */
            name: {
                type: String,
                required: true,
            },
            /**
             * Whether this field is required or not
             */
            required: {
                type: Boolean,
            },
            /**
             * Whether multiple items can be selected
             */
            multiple: {
                type: Boolean,
                default: false,
            },
            /**
             * If multiple, how many can a user select?
             */
            maxSelectable: {
                type: Number,
                default: -1,
            },
            /**
             * Placeholder text shown in the input
             */
            placeholder: {
                type: String,
            },
            /**
             * API endpoint to hit with the query
             */
            searchUri: {
                type: String,
                required: true,
            }
        },
        data: () => {
            return {
                searchQuery: '',
                searchThrottle: null,
                searchThrottleTimeout: 300,
                hideResultsThrottle: null,
                hideResultsThrottleTimeout: 300,
                suggestedSearch: null,
                results: [],
                externalResults: [],
                selected: [],
                selectedData: [],
                inputElementHasFocus: false,
                actionButton: {
                    content: null,
                    ariaLabel: null,
                    callback: null,
                },
                ariaFocusedResult: '',
                noResults: null,
            }
        },
        computed: {
            placeholderText: function () {
                return this.placeholder || '';
            },
            selectAttributes: function () {
                let attributes = {
                    name: this.name,
                };

                if (this.placeholder) {
                    attributes.placeholder = this.placeholder;
                }

                if (this.required) {
                    attributes.required = true;
                }

                if (this.multiple) {
                    attributes.multiple = true;
                    attributes.name += '[]';
                }

                return attributes;
            },
            max: function () {
                return Math.max(1, this.multiple ? this.maxSelectable : 1);
            },
        },
        created() {
            this._isSearching = false;
            this._selected = {};
        },
        mounted() {
            this.$_inputElement = this.$el.querySelector('.input-wrap > input');
        },
        watch: {
            selected() {
                this.$emit('input', this.selected);
            }
        },
        methods: {

            /**
             * Deal with user input, navigating through the list or toggling selection
             *
             * @param key
             *
             * @todo Refactor a bit - the $focusCandidate usage after the switch doesn't make much sense (but we don't want to dupe it in the switch though...)
             */
            $_ariaSelectInteraction: function (key) {

                if (!this.results || this.results.length === 0) {
                    return;
                }

                const $currentlyFocused = this.$el.querySelector('[role="option"][has-input-focus]');
                let $focusCandidate = null;

                switch (key) {
                    //navigate selection
                    case Key.UP:
                        if (!$currentlyFocused || !$currentlyFocused.previousElementSibling || !$currentlyFocused.previousElementSibling.matches('[role="option"]')) {
                            $focusCandidate = this.$el.querySelector('[role="option"]:last-child');
                        } else {
                            $focusCandidate = $currentlyFocused.previousElementSibling;
                        }
                        break;
                    case Key.DOWN:
                        if (!$currentlyFocused || !$currentlyFocused.nextElementSibling || !$currentlyFocused.nextElementSibling.matches('[role="option"]')) {
                            $focusCandidate = this.$el.querySelector('[role="option"]');
                        } else {
                            $focusCandidate = $currentlyFocused.nextElementSibling;
                        }
                        break;

                    //toggle selection
                    case Key.RETURN:
                    case Key.TAB:
                        this.$_toggleSelectionFromResults($currentlyFocused.dataset.resultIndex);
                        break;
                    case Key.ESC:
                        this.$_blurInputElement();
                        break;
                }

                if ($focusCandidate) {
                    for (const $focused of this.$el.querySelectorAll('[role="option"][has-input-focus]')) {
                        $focused.removeAttribute('has-input-focus');
                    }
                    $focusCandidate.setAttribute('has-input-focus', true);
                    this.ariaFocusedResult = $focusCandidate.id;
                }
            },

            /**
             * Defocus any selected items...
             */
            $_ariaDefocus: function () {
                this.ariaFocusedResult = '';
                for (const $focused of this.$el.querySelectorAll('[role="option"][has-input-focus]')) {
                    $focused.removeAttribute('has-input-focus');
                }
            },

            $_inputKeyDown: function (event) {
                const key = event.which || event.keyCode;
                if (!IsAriaListboxInteraction(key)) {
                    return;
                }
                this.$_ariaSelectInteraction(key);

                if (key !== Key.ESC) {
                    event.preventDefault();
                }
            },

            /**
             * Trigger when the query has updated, will call the search with throttle protection
             */
            $_queryUpdate: function () {
                if (this.searchThrottle) {
                    window.clearTimeout(this.searchThrottle);
                }

                if (this.searchQuery.trim() === '') {
                    this.results = [];
                    return;
                }

                this.searchThrottle = window.setTimeout(() => {
                    this.$_search();
                }, this.searchThrottleTimeout);
            },

            /**
             * Perform the search against the given API URI
             */
            $_search: function () {
                //bail for no/short query
                if (this.searchQuery.trim() === '' || this.searchQuery.length < 3) {
                    return;
                }

                if (this._isSearching) {
                    //@todo Should this throw?
                    throw 'Already searching';
                }

                //@todo move this out somewhere a bit more logical
                let token = document.querySelector('[type="hidden"][name="_token"]');
                if (!token) {
                    reject('Could not find CSRF token field in current page');
                    return;
                }

                this.noResults = null;
                this._isSearching = true;
                this.results = [];

                _dispatchDomEvent(this.$el, events.searchStarted, {
                    query: this.searchQuery
                });


                let requestResponse;

                /**
                 * We'd opt to use `.finally()` from the promise, but it seems Vue doesn't reprocess the DOM in that
                 * case (_isSearching doesn't propagate to the template), so we call it manually...
                 * @private
                 */
                const _finally = () => {
                    this._isSearching = false;
                    _dispatchDomEvent(this.$el, events.searchCompleted, {
                        query: this.searchQuery,
                        result: requestResponse
                    });
                };

                Api.local.searchLocations({
                    query: this.searchQuery,
                    token: token.value,
                })
                    .then(data => {
                        requestResponse = data;
                        this.suggestedSearch = data.result.suggested;
                        this.results = data.result.locations;
                        this.noResults = this.results.length === 0;
                        _finally();
                    }, error => {
                        alert('There was an error. Please try again.');
                        console.error(error);
                        _finally();
                    })
                //see `_finally` above...
                //.finally(_finally)
                ;
            },

            /**
             * Clear user selection
             */
            $_wipeSelected: function () {
                this._selected = {};
                this.selected = [];
            },

            /**
             * Update the user selection (this propagates to the <select>)
             */
            $_updateSelectedData: function () {
                //update the data for repaint/access
                let selected = [];
                for (let entry of Object.entries(this._selected)) {
                    selected.push(entry[0]);
                }
                this.selected = selected;


                _dispatchDomEvent(this.$el, events.selectionUpdated, {
                    selection: this._selected
                });
            },

            /**
             * Select an item from the results displayed
             * @param index
             */
            $_toggleSelectionFromResults: function (index) {

                if (this.max === 1) {
                    this.$_wipeSelected();
                }

                const result = this.results[index];

                if (this._selected[result.value]) {
                    delete this._selected[result.value];
                } else {
                    if (this.selected.length >= this.max) {
                        alert(`You can only select up to ${this.max} items`);
                        this.$_focusInputElement();
                        return;
                    }

                    this._selected[result.value] = result;
                }

                this.$_updateSelectedData();

                if (this.max > 1) {
                  this.$_focusInputElement();
                }
            },

            /**
             * Remove an item from the "tag list" of selected items
             * @param value
             */
            $_removeViaSelection: function (value) {
                if (this._selected[value]) {
                    delete this._selected[value];
                }

                this.$_updateSelectedData();
                this.$_focusInputElement();
            },

            /**
             * Focus on the <input> text area. Is connected to whether results can show or not
             */
            $_focusInputElement: function () {
                this.$_inputElement.focus(({preventScroll: true}));
                this.$_cancelDelayedHideResults();
                this.inputElementHasFocus = true;
            },

            /**
             * Blur from the <input> text area
             */
            $_blurInputElement: function () {
                this.$el.focus();
                this.$_delayHideResults();
            },

            /**
             * Hide the results display, but throttle it a tad so that we can blur and refocus without blinking the results
             */
            $_delayHideResults: function () {

                if (this.hideResultsThrottle) {
                    window.clearTimeout(this.hideResultsThrottle);
                }

                this.hideResultsThrottle = window.setTimeout(() => {
                    this.inputElementHasFocus = false;
                    this.$_ariaDefocus();
                }, this.hideResultsThrottleTimeout);
            },

            /**
             * Stop hiding the results...
             */
            $_cancelDelayedHideResults: function () {
                window.clearTimeout(this.hideResultsThrottle);
            },

            /**
             * Is the input focused?
             * @returns {boolean}
             */
            $_inputFocused: function () {
                return document.activeElement === this.$_inputElement;
            },

            /**
             * Try and run the callback for the action button, if it is set. If the search query has changed
             * after the callback, ensure the <input> is focused so results can be displayed
             * @returns {Promise<void | never>}
             */
            $_attemptActionButtonCallback: function (event) {
                event.preventDefault();

                if (typeof this.actionButton.callback !== 'function') {
                    return;
                }

                const currentSearchQuery = this.searchQuery;

                return Promise.resolve()
                    .then(this.actionButton.callback)
                    //if the callback changed the search query, focus the input (this will enable results to display)
                    .then((response) => {
                        if (this.searchQuery !== currentSearchQuery || response) {
                            this.$_focusInputElement();
                        }
                    });
            },

            /**
             * Enable the action button, given the HTML content, aria label, and the callback
             * @param content
             * @param ariaLabel
             * @param callback
             */
            enableActionButton(content, ariaLabel, callback) {
                this.actionButton.ariaLabel = ariaLabel;
                this.actionButton.content = content;
                this.actionButton.callback = callback;
            },

            /**
             * Set the search query, triggers the search
             * @todo Should this $_focusInputElement() as well? Would save and simplify $_attemptActionButtonCallback...
             * @param query
             */
            setSearchQuery: function (query) {
                this.searchQuery = query;
                this.$_queryUpdate();
            },

            $_focusInputFromWrapper: function (event) {
                if (!event.target.classList.contains('input-wrap') && !event.target.classList.contains('tag-list')) {
                    return;
                }

                this.$_focusInputElement();
            },

            /**
             * Takes a click event from `.non-selectable`, checks if it was a query suggestion or not. If yes, it
             * updates the query to that. Also resumes focus on the input
             * @param event
             */
            $_maybeQuerySuggestion: function (event) {
                let depth = 3, element = event.target;

                //check parents too, in case someone clicked on a <strong> inside the suggestion
                do {
                    if (element.classList.contains('suggestion')) {
                        this.setSearchQuery(element.innerText.trim());
                        break;
                    }
                }
                while (--depth > 0 && (element = element.parentNode));


                this.$_focusInputElement();
            },

            /**
             * Inject a result for selection
             */
            injectExternalResult: function (value, label, objectData) {
                label = label || value;

                for (const result of this.externalResults) {
                    if (result.value === value) {
                        return;
                    }
                }

                let data = typeof objectData === 'object' ? objectData : {};

                data.detail = data.detail || label;
                data.label = data.label || label;
                data.value = data.value || value;

                this.externalResults.push(data);
            },

            /**
             * Remove an externally injected result
             */
            removeExternalResult: function (value) {
                for (let i = 0; i < this.externalResults; i++) {
                    if (this.externalResults[i].value === value) {
                        this.externalResults.splice(i, 1);
                        return;
                    }
                }
            },

            /**
             * Mostly used with external interactions...
             * @param value
             */
            toggleSelectionFromValue: function (value) {
                //@todo refactor this (and the same from the other selection method) into a "maybe" style method
                if (this.max === 1) {
                    this.$_wipeSelected();
                }

                let result = null;

                //try find in current results
                for (const candidate of this.results) {
                    if (candidate.value === value) {
                        result = candidate;
                        break;
                    }
                }

                //..not in current results, try in externally injected results
                if (!result) {
                    for (const candidate of this.externalResults) {
                        if (candidate.value === value) {
                            result = candidate;
                            break;
                        }
                    }
                }

                if (this._selected[result.value]) {
                    delete this._selected[result.value];
                } else {
                    if (this.selected.length >= this.max) {
                        alert(`You can only select up to ${this.max} items`);
                        // this.$_focusInputElement();
                        return false;
                    }

                    this._selected[result.value] = result;
                }

                this.$_updateSelectedData();
                return true;
            }
        }
    }
</script>

<style lang="scss" scoped>

    .auto-suggest-input {

      ::v-deep {
            $gap: 5px;

            $background-color: #e3e3e3;
            $border-color: #e1e1e1;
            $divider-color: $border-color; //lighten($border-color, 0.2);
            $selectable-background-color: $divider-color;
            $selected-color: rgba(0, 147, 0, 0.5);

            position: relative;
            display: inline-block;

            > select {
                //appearance is required to squash the "unfocusable element" error that Chrome now shows
                opacity: 0;
                position: absolute;
                border: 0;
                overflow: hidden;
                top: 0;
                right: 0;
                bottom: 0;
                left: 0;
            }

            .input-wrap {
                display: inline-flex;
                flex-direction: row;
                flex-wrap: wrap;
                max-width: 100%;
                position: relative;
                cursor: text;

                border-width: 1px;
                border-style: inset;
                border-color: initial;
                border-image: initial;

                padding: $gap $gap 0;

                input, button, textarea, .tag-list {
                    appearance: none;
                    background: transparent;
                    border: 0;
                    box-shadow: none;
                    padding: 0;
                    margin: 0;
                    flex-grow: 1;

                    &:focus {
                        border: 0;
                        box-shadow: none;
                    }
                }

                input[type="text"] {
                    margin-bottom: $gap;
                    max-width: 100%;
                }

                &.with-selection {
                    input[type="text"], button.action-button {
                        position: absolute;
                        top: -500em;
                    }

                    &.focused {
                        input[type="text"], button.action-button {
                            position: static;
                            top: auto;
                        }
                    }
                }

                input + button.action-button {
                    white-space: nowrap;
                    text-transform: uppercase;
                    background: $background-color;
                    flex-grow: 0;
                    margin: $gap;
                }

                + .results {
                    display: none;
                    position: absolute;
                    z-index: 1;
                    top: 100%;
                    left: 0;
                    right: 0;
                    /*max-height: 300px;*/
                    overflow: auto;

                    border: 1px solid $border-color;

                    &.no-results, &.has-results, &.loading-results {
                        display: block;
                    }

                    @media (max-width: 767px) {
                        position: relative;
                        top: auto;
                        left: auto;
                        right: auto;
                        max-height: none;
                        overflow: visible;
                    }

                    background: white;

                    .throbber {
                        display: none;
                        text-align: center;
                        padding: 2px;
                    }

                    .no-results-message {
                        margin: 0;
                        padding: $gap * 2;
                        em {
                            font-weight: bold;
                        }
                    }

                    &.loading-results {
                        .throbber {
                            padding: $gap * 2;
                            display: block;
                        }

                        ul, ol {
                            display: none;
                        }
                    }

                    ul, ol {
                        &, li {
                            list-style: none;
                            margin: 0;
                            padding: 0;
                        }

                        li {
                            padding: $gap;
                            user-select: none;
                            border-bottom: 1px solid $divider-color;

                            > span {
                                display: block;
                            }

                            &.not-selectable {
                                cursor: default;
                                background: $selectable-background-color;
                                border-bottom: 1px solid $divider-color;
                            }

                            &.selectable {
                                padding: $gap $gap*2;
                                position: relative;

                                &:hover, &[has-input-focus] {
                                    background: $selectable-background-color;
                                    cursor: pointer;
                                }

                                &.selected {
                                    font-weight: bold;
                                    //background: $selected-color;

                                    &:before, &:after {
                                        content: '';
                                        position: absolute;
                                        top: 0;
                                        bottom: 0;
                                        left: 0;
                                        width: $gap;

                                        background: $selected-color;
                                    }

                                    &:after {
                                        left: auto;
                                        right: 0;
                                    }
                                }
                            }

                            &:last-child {
                                border: 0;
                            }


                            .suggestion {
                                position: relative;
                                z-index: 1;
                                cursor: pointer;
                                display: inline-block;

                                &:before {
                                    content: '';
                                    position: absolute;
                                    top: 0;
                                    left: -2.5px;
                                    right: -2.5px;
                                    bottom: 0;
                                    background: rgba(0, 0, 0, 0.05);
                                    z-index: -1;
                                    border-radius: 5px;
                                }

                                &:hover {
                                    &:before {
                                        background: rgba(0, 0, 0, 0.1);
                                    }
                                }
                            }
                        }
                    }
                }


                ul.tag-list {

                    &, li {
                        list-style: none;
                        margin: 0;
                        padding: 0;
                    }

                    & {
                        margin-bottom: $gap;
                    }

                    > li {
                        display: inline-block;
                        white-space: nowrap;
                        font-size: 8px;
                        text-transform: uppercase;
                        background: $background-color;
                        border-radius: 2px;
                        cursor: pointer;
                        padding-left: $gap;
                        padding-right: $gap;
                        margin-bottom: $gap;
                        margin-right: $gap * 2;

                        > .remove-tag {
                            display: inline-block;
                            text-align: center;
                            $size: 2em;
                            appearance: none;
                            background: transparent;
                            border: 0;
                            padding: 0;
                            width: $size;
                            height: $size;
                            line-height: $size;
                        }

                        &:hover {
                            > .remove-tag {
                                background: rgba(255, 255, 255, 0.3);
                            }
                        }
                    }
                }
            }
        }
    }
</style>
